/**
 * Hook reutilizable para funcionalidades del chat
 * - Copiar/pegar en el input
 * - Reproducir audio al hacer click en burbujas
 */

import { useCallback, useRef } from 'react';
import { ChatMessage } from '../types';
import { webSocketService } from '../services/webSocketService';

interface UseChatFeaturesOptions {
  /**
   * Habilitar copiar/pegar en el input
   * Útil para modo estudio donde el usuario puede copiar texto de otras fuentes
   */
  enableCopyPaste?: boolean;
  
  /**
   * Habilitar reproducir audio al hacer click en burbujas
   * Útil para todos los modos donde hay respuestas de audio
   */
  enableAudioReplay?: boolean;
  
  /**
   * Modo actual de práctica
   * Algunas funcionalidades pueden variar según el modo
   */
  mode?: string;
}

interface UseChatFeaturesReturn {
  /**
   * Handler para eventos de pegar en el input
   * Extrae texto del clipboard y lo inserta en el input
   */
  handlePaste: (e: React.ClipboardEvent<HTMLInputElement>, setInputText: (text: string) => void) => void;
  
  /**
   * Handler para click en burbujas de mensajes
   * Reproduce el audio asociado al mensaje si está disponible
   */
  handleMessageClick: (message: ChatMessage) => void;
  
  /**
   * Handler para copiar texto de un mensaje
   * Copia el contenido del mensaje al clipboard
   */
  handleCopyMessage: (message: ChatMessage) => void;
}

/**
 * Hook reutilizable para funcionalidades del chat
 * 
 * @param options - Opciones de configuración
 * @returns Funciones y handlers para usar en componentes de chat
 */
export function useChatFeatures(options: UseChatFeaturesOptions = {}): UseChatFeaturesReturn {
  const {
    enableCopyPaste = true,
    enableAudioReplay = true,
    // mode - reservado para futuras funcionalidades específicas por modo
  } = options;
  
  // Referencia para el audio actual que se está reproduciendo
  const currentReplayAudioRef = useRef<HTMLAudioElement | null>(null);
  
  /**
   * Manejar evento de pegar en el input
   * Extrae texto del clipboard y lo inserta en el input
   */
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>, setInputText: (text: string) => void) => {
    if (!enableCopyPaste) {
      return; // No hacer nada si está deshabilitado
    }
    
    // Permitir pegar texto desde el clipboard
    const pastedText = e.clipboardData.getData('text/plain');
    
    if (pastedText) {
      e.preventDefault(); // Prevenir el comportamiento por defecto
      
      // Insertar el texto pegado en el input
      // Obtener el input del evento
      const input = e.currentTarget;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const currentValue = input.value;
      
      // Insertar texto en la posición del cursor
      const newValue = currentValue.substring(0, start) + pastedText + currentValue.substring(end);
      setInputText(newValue);
      
      // Restaurar posición del cursor después del texto pegado
      setTimeout(() => {
        const newCursorPos = start + pastedText.length;
        input.setSelectionRange(newCursorPos, newCursorPos);
        input.focus();
      }, 0);
      
    }
  }, [enableCopyPaste]);
  
  /**
   * Manejar click en burbujas de mensajes para reproducir audio
   */
  const handleMessageClick = useCallback((message: ChatMessage) => {
    if (!enableAudioReplay) {
      return; // No hacer nada si está deshabilitado
    }
    
    // Solo reproducir si el mensaje tiene audio y es del asistente
    if (message.role === 'assistant') {
      // Detener audio manual anterior si está reproduciendo
      if (currentReplayAudioRef.current) {
        currentReplayAudioRef.current.pause();
        currentReplayAudioRef.current = null;
      }
      
      // Detener audio automático si está reproduciendo
      webSocketService.stopAutomaticAudio();
      
      // Notificar que vamos a reproducir audio manual
      webSocketService.setManualAudioPlaying(true);
      
      // Priorizar audioUrl individual sobre audioUrls array
      let audioUrlToUse: string | null = null;
      
      if (message.audioUrl) {
        // Mensaje con audio individual
        audioUrlToUse = message.audioUrl;
      } else if (message.audioUrls && message.audioUrls.length > 0) {
        // Mensaje consolidado con múltiples audios - reproducir el primero
        // TODO: En el futuro se podría hacer un reproductor que reproduzca todos en secuencia
        audioUrlToUse = message.audioUrls[0];
      }
      
      if (!audioUrlToUse) {
        return;
      }
      
      // Verificar si la URL original funciona, si no, recrear desde el blob
      let recreatedUrl: string | null = null;
      const originalUrl = audioUrlToUse;
      const recreated = webSocketService.recreateAudioUrl(audioUrlToUse);
      if (recreated) {
        recreatedUrl = recreated;
        audioUrlToUse = recreated;
      }
      
      // Crear nuevo elemento de audio y reproducir
      const audio = new Audio(audioUrlToUse);
      currentReplayAudioRef.current = audio;
      
      audio.onended = () => {
        currentReplayAudioRef.current = null;
        // Notificar que el audio manual terminó
        webSocketService.setManualAudioPlaying(false);
        // Si recreamos la URL, revocarla después de usar
        if (recreatedUrl && recreatedUrl !== originalUrl) {
          URL.revokeObjectURL(recreatedUrl);
        }
      };
      
      audio.onerror = (error) => {
        console.error('[ChatFeatures] Error al reproducir audio del mensaje:', error);
        currentReplayAudioRef.current = null;
        // Notificar que el audio manual terminó (por error)
        webSocketService.setManualAudioPlaying(false);
        // Si recreamos la URL, revocarla en caso de error
        if (recreatedUrl && recreatedUrl !== originalUrl) {
          URL.revokeObjectURL(recreatedUrl);
        }
      };
      
      audio.play().catch((error) => {
        console.error('[ChatFeatures] Error al iniciar reproducción:', error);
        currentReplayAudioRef.current = null;
        // Notificar que el audio manual no pudo iniciarse
        webSocketService.setManualAudioPlaying(false);
        // Si recreamos la URL, revocarla en caso de error
        if (recreatedUrl && recreatedUrl !== originalUrl) {
          URL.revokeObjectURL(recreatedUrl);
        }
      });
      
    }
  }, [enableAudioReplay]);
  
  /**
   * Copiar texto de un mensaje al clipboard
   */
  const handleCopyMessage = useCallback((message: ChatMessage) => {
    if (!message.content) {
      return;
    }
    
    navigator.clipboard.writeText(message.content).then(() => {
      // Opcional: mostrar notificación visual
    }).catch((error) => {
      console.error('[ChatFeatures] Error al copiar texto:', error);
    });
  }, []);
  
  return {
    handlePaste,
    handleMessageClick,
    handleCopyMessage,
  };
}

